import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Shield, Settings, Clock, Award, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const Home = () => {
  const features = [
    { icon: Clock, title: "Extensive Industry Expertise", desc: "Decades of combined experience in power solutions" },
    { icon: Settings, title: "Modern Manufacturing", desc: "State-of-the-art production facilities" },
    { icon: Zap, title: "High Accuracy Regulation", desc: "Precise voltage stabilization up to ±1%" },
    { icon: Shield, title: "Reliable Support", desc: "Comprehensive after-sales service" },
    { icon: Award, title: "Tailor Made Solutions", desc: "Custom stabilizers for specific needs" },
    { icon: MapPin, title: "Made in India", desc: "Designed and manufactured domestically" },
  ];

  const products = [
    {
      title: "Single Phase Servo Stabilizers",
      desc: "Reliable voltage correction for domestic and light industrial applications with capacities from 1 KVA to 25 KVA.",
      link: "/products/single-phase",
      image: "https://images.unsplash.com/photo-1570086625762-7c1396540ac5?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    {
      title: "Three Phase Servo Stabilizers",
      desc: "Designed for commercial and industrial environments requiring stable voltage regulation from 6 KVA to 100 KVA.",
      link: "/products/three-phase",
      image: "https://images.unsplash.com/photo-1684430598409-750ae3284704?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    {
      title: "Three Phase Oil Cooled Servo Stabilizers",
      desc: "Heavy-duty stabilizers for continuous operation with superior thermal efficiency, 6 KVA to 500 KVA.",
      link: "/products/oil-cooled",
      image: "https://images.unsplash.com/photo-1671351966453-3f1410780c41?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
  ];

  const applications = [
    "Residential Homes",
    "Hospitals & Diagnostic Centers",
    "IT & Server Rooms",
    "Heavy Industrial Units",
    "Educational Institutions",
    "Commercial Complexes",
    "Manufacturing Facilities",
    "Data Centers",
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-white py-20 lg:py-28 overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-primary-main mb-6 tracking-tight leading-tight">
                STABLE POWER.
                <br />
                <span className="text-secondary-dark">ABSOLUTE PROTECTION.</span>
              </h1>
              <p className="text-lg text-secondary-main font-body mb-8 leading-relaxed">
                Advanced Servo Voltage Stabilizers for Residential, Commercial and Industrial Applications.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  asChild
                  className="bg-primary-main text-white hover:bg-primary-light rounded-sm px-6 py-6 text-base"
                  data-testid="hero-view-products-button"
                >
                  <Link to="/products">
                    View Products
                    <ArrowRight className="ml-2" size={18} />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-primary-main text-primary-main hover:bg-primary-main hover:text-white rounded-sm px-6 py-6 text-base"
                  data-testid="hero-send-enquiry-button"
                >
                  <Link to="/contact">Send Enquiry</Link>
                </Button>
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1767294274634-613a3545e36d?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Modern industrial facility"
                className="rounded-sm shadow-lg w-full h-[400px] object-cover"
                data-testid="hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-16 bg-white" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary-main mb-6">
              ABOUT VON
            </h2>
            <p className="text-base text-secondary-main font-body leading-relaxed mb-6">
              VON is dedicated to delivering reliable and efficient power conditioning solutions for residential,
              commercial and industrial sectors.
            </p>
            <p className="text-base text-secondary-main font-body leading-relaxed mb-8">
              With decades of combined industry experience, we manufacture high-performance Servo Voltage Stabilizers
              that protect electrical and electronic equipment from voltage fluctuations. Supported by advanced
              engineering, premium components and strict quality control, VON stabilizers deliver precise voltage
              regulation, improved equipment life and uninterrupted performance.
            </p>
            <Button
              asChild
              variant="outline"
              className="border-primary-main text-primary-main hover:bg-primary-main hover:text-white rounded-sm"
              data-testid="about-learn-more-button"
            >
              <Link to="/about">Learn More About Us</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Product Range */}
      <section className="py-16 bg-gray-50" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary-main mb-4">
              OUR PRODUCT RANGE
            </h2>
            <p className="text-base text-secondary-main font-body max-w-2xl mx-auto">
              Comprehensive voltage stabilization solutions engineered for precision and reliability
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-sm overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`product-card-${index}`}
              >
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-heading font-bold text-primary-main mb-3">
                    {product.title}
                  </h3>
                  <p className="text-sm text-secondary-main font-body mb-4 leading-relaxed">
                    {product.desc}
                  </p>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full border-primary-main text-primary-main hover:bg-primary-main hover:text-white rounded-sm"
                    data-testid={`product-button-${index}`}
                  >
                    <Link to={product.link}>View Details</Link>
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose VON */}
      <section className="py-16 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary-main mb-4">
              WHY CHOOSE VON
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex gap-4 items-start"
                  data-testid={`feature-${index}`}
                >
                  <div className="bg-primary-main/10 p-3 rounded-sm flex-shrink-0">
                    <Icon className="text-primary-main" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-primary-main mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-secondary-main font-body">
                      {feature.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-16 bg-gray-50" data-testid="applications-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary-main mb-4">
              APPLICATIONS
            </h2>
            <p className="text-base text-secondary-main font-body max-w-2xl mx-auto">
              Serving diverse sectors where voltage stability is critical
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {applications.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-gray-200 rounded-sm p-4 text-center hover:border-primary-main transition-colors"
                data-testid={`application-${index}`}
              >
                <p className="font-body font-medium text-secondary-dark">{app}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
